/**
 * Catalogue discovery.
 *
 * **Adding a language is adding a directory, and nothing else.** No list of
 * languages in code, no `switch`, no import to remember — the catalogues under
 * `src/locales/` *are* the set, and this module reads them. That is the constraint
 * the maintainer set on 2026-09-27: the set of languages is not decided, Swedish
 * and English are the minimum, and nothing may be built that makes the second
 * language cheap and the third expensive (`docs/STATUS.md`).
 *
 * It also gives `I18N.md` §6 the thing it asks for: a language selector on the
 * login screen that *enumerates catalogues* rather than listing languages in code.
 * The selector itself belongs to phase 1 — there is no login screen yet — but
 * {@link languages} and {@link languageName} are what it will read, so the shape is
 * settled now rather than retrofitted.
 *
 * **Bundled, never fetched.** `import.meta.glob` with `eager` puts every catalogue
 * in the bundle at build time. There is deliberately no `i18next-http-backend` and
 * no lazy `import()`: the box has no internet at run time (`AGENTS.md` §3 rule 3),
 * and a catalogue that arrives over the network is a screen that renders keys when
 * it does not. At a few kilobytes each this costs nothing worth measuring.
 *
 * **JSON carries no comments**, which is the one thing lost in the move from the
 * old `strings.ts`. The reason a string is worded the way it is now lives with the
 * code that chooses it — the allergy trichotomy in `ward/wardDisplay.ts`, the
 * `notRecorded` / `unreadable` split in `ward/WardList.tsx`. JSON is still right:
 * `I18N.md` §1 specifies it, and §7 sends these files to a professional translator
 * in phase 3, who cannot be handed a TypeScript module.
 */

/** One namespace's messages, as loaded. Values are strings or nested objects. */
export type Catalogue = Readonly<Record<string, unknown>>

const files = import.meta.glob<Catalogue>('../locales/*/*.json', {
  eager: true,
  import: 'default',
})

/**
 * The catalogue everything else is measured against.
 *
 * Swedish, because it is the one clinicians review (`I18N.md` §7) and therefore the
 * only one that can be the source of truth for what keys exist. It is also the
 * fallback: a missing English key shows Swedish text, which is visibly wrong to a
 * reader and therefore reportable, where a rendered key name looks like a crash.
 */
export const REFERENCE_LANGUAGE = 'sv'

/** The namespace a lookup uses when it does not name one. */
export const DEFAULT_NAMESPACE = 'common'

export interface DiscoveredCatalogue {
  readonly language: string
  readonly namespace: string
  /** The glob path, for error messages that have to name a file. */
  readonly path: string
  readonly messages: Catalogue
}

const CATALOGUE_PATH = /\/locales\/([^/]+)\/([^/]+)\.json$/

function discover(): readonly DiscoveredCatalogue[] {
  const found = Object.entries(files).map(([path, messages]) => {
    const match = CATALOGUE_PATH.exec(path)
    const language = match?.[1]
    const namespace = match?.[2]

    if (language === undefined || namespace === undefined) {
      throw new Error(`Catalogue '${path}' is not locales/<language>/<namespace>.json`)
    }

    return { language, namespace, path, messages }
  })

  if (found.length === 0) {
    // An empty discovery would initialise i18next with no messages and render key
    // names on every screen. Failing loudly at import is the lesser evil, and it is
    // `preflight.sh`'s rule again: a check that could not run is not a check that
    // passed.
    throw new Error('No catalogues found under src/locales — i18n would render key names.')
  }

  return found
}

export const catalogues: readonly DiscoveredCatalogue[] = discover()

/**
 * Every language that has a catalogue, reference language first, then alphabetical.
 *
 * The order is the order a selector offers them in, so it must not depend on the
 * filesystem's traversal order.
 */
export const languages: readonly string[] = [
  ...new Set(catalogues.map((catalogue) => catalogue.language)),
].sort((left, right) => {
  if (left === REFERENCE_LANGUAGE) return -1
  if (right === REFERENCE_LANGUAGE) return 1
  return left.localeCompare(right, 'en')
})

/** Every namespace the reference language defines. */
export const namespaces: readonly string[] = [
  ...new Set(
    catalogues
      .filter((catalogue) => catalogue.language === REFERENCE_LANGUAGE)
      .map((catalogue) => catalogue.namespace),
  ),
].sort((left, right) => left.localeCompare(right, 'en'))

/** The discovered catalogues in the shape i18next's `resources` option wants. */
export const resources: Readonly<Record<string, Readonly<Record<string, Catalogue>>>> =
  catalogues.reduce<Record<string, Record<string, Catalogue>>>((accumulated, catalogue) => {
    const forLanguage = (accumulated[catalogue.language] ??= {})
    forLanguage[catalogue.namespace] = catalogue.messages
    return accumulated
  }, {})

/**
 * A language's name **in that language** — `Svenska`, not `Swedish`.
 *
 * Read out of the catalogue's own `language.name` key, so adding a language adds
 * its own label and no translation matrix is needed. A selector that showed
 * `Spanska` to a Spanish-speaking nurse arriving on the ward would be the one
 * place it matters, and that is precisely `I18N.md` §6's scenario.
 *
 * Falls back to the code so a half-finished catalogue still appears in a selector
 * rather than vanishing from it.
 */
export function languageName(language: string): string {
  const common = resources[language]?.[DEFAULT_NAMESPACE]
  const block = common?.['language']
  if (typeof block === 'object' && block !== null) {
    const name = (block as Record<string, unknown>)['name']
    if (typeof name === 'string' && name !== '') {
      return name
    }
  }
  return language
}
